/** Step 1, past sessions (2.J deliverable 12): a count, the first number, and a row for each session. */
import { BOOTSTRAP_SESSIONS_MAX } from '@codex/contracts';
import { Button, TextField } from '@codex/ui';
import { SESSIONS_KIND, sessionsApi, useBatches, useSessions } from '../data.js';
import { useDraft } from '../drafts.js';
import { batchRows } from '../model/step-undo.js';
import {
  issueText,
  nextStart,
  type SessionRow,
  sessionRows,
  sessionsRequest,
} from '../model/wizard.js';
import { keepsDateText } from '../preview-table.js';
import { StepPanel } from '../step-panel.js';
import { AppliedList, undoBatch } from '../step-undo.js';
import { useStep } from '../use-step.js';

const asNumber = (text: string, fallback: number) => {
  const n = Number.parseInt(text, 10);
  return Number.isFinite(n) ? n : fallback;
};

export function SessionsStep() {
  const step = useStep(sessionsApi);
  const sessions = useSessions();
  const batches = useBatches(SESSIONS_KIND);
  const [countText, setCountText] = useDraft('sessions:count', '1');
  const [startText, setStartText] = useDraft<string | null>('sessions:start', null);
  const [typed, setTyped] = useDraft<readonly SessionRow[]>('sessions:typed', []);

  const count = Math.min(Math.max(asNumber(countText, 1), 1), BOOTSTRAP_SESSIONS_MAX);
  const start = Math.max(
    asNumber(startText ?? '', nextStart((sessions.data ?? []).map((s) => s.number))),
    1,
  );
  const rows = sessionRows(count, start, typed);
  const kept = new Set(
    (step.state.preview?.rows ?? []).filter((r) => r.dateKept === true).map((r) => r.number),
  );
  const change = (update: () => void) => {
    update();
    step.edit();
  };
  const setRow = (row: SessionRow) =>
    change(() => setTyped([...typed.filter((r) => r.number !== row.number), row]));

  return (
    <form
      className="cx-bootstrap__step"
      onSubmit={(event) => {
        event.preventDefault();
        if (sessions.isSuccess) void step.preview(sessionsRequest(rows));
      }}
    >
      <p className="cx-bootstrap__muted">
        Tell Codex how many sessions you played before it existed. Each gets a number, and a date
        and title if you know them. Nothing is written until you press Apply.
      </p>
      <div className="cx-bootstrap__fields">
        <TextField
          label="How many sessions"
          type="number"
          min={1}
          max={BOOTSTRAP_SESSIONS_MAX}
          data-testid="bootstrap.sessions.count"
          value={countText}
          onChange={(e) => change(() => setCountText(e.target.value))}
        />
        <TextField
          label="First session number"
          type="number"
          min={1}
          value={startText ?? String(start)}
          onChange={(e) => change(() => setStartText(e.target.value))}
        />
      </div>
      {sessions.isPending && (
        <p role="status" aria-label="Loading sessions" className="cx-bootstrap__muted">
          Loading your sessions…
        </p>
      )}
      {sessions.isError && (
        <div role="alert" className="cx-bootstrap__error">
          <span>Couldn't load your sessions.</span>{' '}
          <Button size="sm" onClick={() => void sessions.refetch()}>
            Try again
          </Button>
        </div>
      )}
      <ol className="cx-bootstrap__rows" aria-label="Sessions" hidden={!sessions.isSuccess}>
        {rows.map((row, i) => {
          const issues = step.state.issues.filter((x) => x.index === i);
          return (
            <li key={row.number} className="cx-bootstrap__session-row">
              <span className="cx-bootstrap__number">{`Session ${row.number}`}</span>
              <TextField
                label={`Date of session ${row.number}`}
                hideLabel
                type="date"
                value={row.realDate}
                onChange={(e) => setRow({ ...row, realDate: e.target.value })}
              />
              <TextField
                label={`Title of session ${row.number}`}
                hideLabel
                placeholder={`Session ${row.number}`}
                maxLength={200}
                value={row.title}
                onChange={(e) => setRow({ ...row, title: e.target.value })}
              />
              {issues.map((x) => (
                <span key={x.code} role="alert" className="cx-bootstrap__error">
                  {issueText(x.code)}
                </span>
              ))}
              {kept.has(row.number) && (
                <span role="alert" className="cx-bootstrap__error">
                  {keepsDateText(row.number)}
                </span>
              )}
            </li>
          );
        })}
      </ol>
      <StepPanel
        state={step.state}
        canApply={step.canApply}
        previewDisabled={!sessions.isSuccess}
        inlineIssues
        onApply={() => void step.apply()}
      >
        <AppliedList
          rows={batchRows(batches.data ?? [], SESSIONS_KIND)}
          undo={(row) => undoBatch(row.id)}
          onUndone={() => void step.refresh()}
        />
      </StepPanel>
    </form>
  );
}
